import React from 'react';
import { ChevronRight } from 'lucide-react';
import '../../styles/enterprise.css';

/**
 * Summary card: tinted round icon, label, large value, and a share/delta chip
 * beside it, over a quiet sub-line.
 *
 * <StatCard
 *   tone="ok"                       // 'brand' | 'ok' | 'warn' | 'err' | 'info'
 *   icon={CheckCircle2}             // a lucide component, not an element
 *   label="Completed"
 *   value="$1,760.00"
 *   chip="89%"                      // optional, omit when there is nothing true to show
 *   sub="of total"
 * />
 *
 * `chip` is deliberately not computed here: a card should never invent a
 * comparison the caller could not derive from real data.
 *
 * `onAction` adds the corner chevron. Omit it and no chevron renders — an arrow
 * that goes nowhere is worse than none, so the affordance only appears when the
 * caller has somewhere to send the click. `actionLabel` names the destination
 * for screen readers, since a bare chevron says nothing.
 */
const StatCard = ({
  tone = 'brand',
  icon: Icon,
  label,
  value,
  chip,
  chipTone,
  sub,
  loading,
  onAction,
  actionLabel,
  // Colours the figure itself. Separate from `tone`, which only tints the icon:
  // an overdue total needs the number to carry the warning, not just the badge
  // beside it.
  valueTone,
  // Optional sparkline, rendered flush along the bottom edge. Absolutely placed
  // rather than added as a row so the cards that pass nothing keep the exact
  // layout they already have.
  chart,
}) => (
  <div
    className={`stat-card stat-card--${tone}${onAction ? ' stat-card--linked' : ''}${
      chart ? ' stat-card--charted' : ''
    }`}
  >
    {Icon ? (
      <span className="stat-card__icon" aria-hidden="true">
        <Icon size={20} />
      </span>
    ) : null}
    <div className="stat-card__body">
      <span className="stat-card__label">{label}</span>
      <div className="stat-card__valrow">
        <span className="stat-card__value">{loading ? '—' : value}</span>
        {!loading && chip ? (
          <span className={`stat-card__chip ${chipTone ? `stat-card__chip--${chipTone}` : ''}`}>
            {chip}
          </span>
        ) : null}
      </div>
      {sub ? <span className="stat-card__sub">{sub}</span> : null}
    </div>
    {chart ? <div className="stat-card__chart">{chart}</div> : null}
    {onAction ? (
      <button
        type="button"
        className="stat-card__go"
        onClick={onAction}
        aria-label={actionLabel || `View ${label}`}
        title={actionLabel || `View ${label}`}
      >
        <ChevronRight size={16} />
      </button>
    ) : null}
  </div>
);

export const StatCardGrid = ({ children }) => <div className="stat-card-grid">{children}</div>;

export default StatCard;
